import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import YellowAlert from '../ui-elements/YellowAlert'
import Card from '../ui-elements/Card'
import CollapsableCheckPeriodSummary from './CollapsableCheckPeriodSummary'
import { sortAllQuestionAnswersForCheckPeriod } from '../utils/sortAllQuestionAnswersForCheckPeriod'
import { getRequiredGroupsForChecklist, userMeetsRequiredGroups } from '../utils/getRequiredGroupsForChecklist'
import { getLoggedInUserGroupIds } from '../utils/getLoggedInUserGroupIds'



type Props = {
}

function ProcessSummaryCheckPeriods({
}: Props) {

    const { processId, processRecordId } = useParams()
    const navigate = useNavigate()
    const {
        tableData,
        userData,
        sendMessageToWebsocket,
        setShowModal,
        processData,
        checkIfThereAreQuestionsAndAnswersForChecklistPeriodId,
        t
    } = useContext(GlobalContext)
    const [waitingForChecklistId, setWaitingForChecklistId] = useState<string | null>(null)
    const [existingPeriodIds, setExistingPeriodIds] = useState<string[]>([])
    const [waitingForFirstQuestionPeriodId, setWaitingForFirstQuestionPeriodId] = useState<string | null>(null)


    const thisProcessData = processId && processData && processData[processId]
    const activeRecordData = thisProcessData && (processRecordId
        ? (thisProcessData.processRecords && thisProcessData.processRecords[processRecordId])
        : thisProcessData)
    const inProgress = !!activeRecordData
    const currentStageId = activeRecordData && activeRecordData.processRecord && activeRecordData.processRecord.CurrentStageId
    const thisStageDetails = thisProcessData && thisProcessData.Stages && currentStageId && thisProcessData.Stages[currentStageId]
    const isAdhoc = thisStageDetails && thisStageDetails.Behaviour === 'ongoing' && thisStageDetails.timedOrAdhoc === 'adhoc'
    const stageChecklists: string[] = (thisStageDetails && thisStageDetails.Checklists) || []

    const processRecordIdForStage = activeRecordData && activeRecordData.processRecord && activeRecordData.processRecord.Id
    const linkedPendingCheckPeriods: any[] = (activeRecordData && activeRecordData.PendingCheckPeriods) || []
    const linkedPendingIds = linkedPendingCheckPeriods.map((p: any) => p.Id)

    // open check periods for this record and stage that are not yet linked in the process record's StageProgress
    const unlinkedOpenCheckPeriods = tableData && tableData.ChecklistPeriods && processRecordIdForStage && currentStageId
        ? Object.values(tableData.ChecklistPeriods).filter((p: any) =>
            p.ProcessRecordId === processRecordIdForStage &&
            p.StageId === currentStageId &&
            p.InProgress === 'True' &&
            !(p.Progress && p.Progress.progress_percentage == 100) &&
            !linkedPendingIds.includes(p.Id)
        )
        : []

    const pendingCheckPeriods = activeRecordData && [...linkedPendingCheckPeriods, ...unlinkedOpenCheckPeriods].sort((a: any, b: any) => {
        return b.StartTimeStamp - a.StartTimeStamp
    })
    const completedCheckPeriods = activeRecordData && activeRecordData.CompletedCheckPeriods && activeRecordData.CompletedCheckPeriods.sort((a: any, b: any) => {
        return b.StartTimeStamp - a.StartTimeStamp
    })

    const hasPendingCheckPeriods = pendingCheckPeriods && pendingCheckPeriods.length > 0
    const isStartingChecklist = waitingForChecklistId !== null || waitingForFirstQuestionPeriodId !== null
    // blocks a second start request sent before the state update above has re-rendered
    const startRequestSent = useRef(false)

    useEffect(() => {
        if (!isStartingChecklist) startRequestSent.current = false
    }, [isStartingChecklist])

    // Phase 1: detect the new check period appearing, then trigger a fetch of its questions/answers
    useEffect(() => {
        if (waitingForChecklistId && pendingCheckPeriods) {
            const newPeriod = pendingCheckPeriods.find(
                (p: any) => p.ChecklistId === waitingForChecklistId && !existingPeriodIds.includes(p.Id)
            )
            if (newPeriod) {
                setWaitingForChecklistId(null)
                setWaitingForFirstQuestionPeriodId(newPeriod.Id)
                checkIfThereAreQuestionsAndAnswersForChecklistPeriodId(newPeriod.Id)
            }
        }
    }, [pendingCheckPeriods, waitingForChecklistId])

    // Phase 2: once answers are loaded for the new period, navigate to the first question
    useEffect(() => {
        if (waitingForFirstQuestionPeriodId && tableData) {
            const sorted = sortAllQuestionAnswersForCheckPeriod(waitingForFirstQuestionPeriodId, tableData)
            if (sorted && sorted.length > 0) {
                const firstAnswer = sorted[0]
                setShowModal(null)
                setWaitingForFirstQuestionPeriodId(null)
                navigate(`/checklists/${waitingForFirstQuestionPeriodId}/${firstAnswer.Id}`)
            }
        }
    }, [tableData, waitingForFirstQuestionPeriodId])

    const startAdhocChecklist = (checklistId: string) => {
        if (startRequestSent.current || isStartingChecklist || hasPendingCheckPeriods) return
        startRequestSent.current = true
        const currentIds = pendingCheckPeriods ? pendingCheckPeriods.map((p: any) => p.Id) : []
        setExistingPeriodIds(currentIds)
        setWaitingForChecklistId(checklistId)
        setShowModal({ spinner: t('General.loading') })
        sendMessageToWebsocket(JSON.stringify({
            action: 'processesClient',
            subAction: 'startAdhocChecklist',
            checklistId,
            processRecordId: activeRecordData.processRecord.Id,
            stageId: currentStageId,
            processDefinitionId: activeRecordData.processRecord.ProcessDefinitionId,
        }))
    }

 

    const totalCheckPeriods = (pendingCheckPeriods && pendingCheckPeriods.length) + (completedCheckPeriods && completedCheckPeriods.length)

    if (totalCheckPeriods === 0 && !isAdhoc) {
        return null
    }

    return <div className={`w-full flex-1 h-full flex flex-col items-center gap-4`}>

        {isAdhoc && stageChecklists.length > 0 && !hasPendingCheckPeriods && <div className={`w-full flex flex-col gap-2`}>
            <p className={`font-bold`}>{t('ProcessPages.ProcessSummaryCheckPeriods.startACheck')}</p>
            {stageChecklists.map((checklistId: string) => {
                const thisChecklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
                const checklistName = thisChecklist && thisChecklist['ChecklistName']
                const requiredGroupIds = getRequiredGroupsForChecklist(thisChecklist, tableData && tableData.ChecklistCategories)
                const isEligible = userMeetsRequiredGroups(getLoggedInUserGroupIds(tableData, userData), requiredGroupIds)
                return <div key={checklistId} className={`w-full flex flex-col gap-1`}>
                    <Button
                        onClick={() => isEligible && startAdhocChecklist(checklistId)}
                        text={checklistName || checklistId}
                        fullwidth={true}
                        size={`big`}
                        variant={isEligible && !isStartingChecklist ? `primary` : `gray`}
                        disabled={!isEligible || isStartingChecklist}
                    />
                    {!isEligible && <p className={`text-xs text-gray-500`}>🔐 {t('ProcessPages.ProcessSummaryCheckPeriods.restrictedToGroup')}</p>}
                </div>
            })}
        </div>}

        {pendingCheckPeriods && pendingCheckPeriods.length > 0 && <>
        <h4 className={`text-xl font-righteous text-brandblue`}>{t('ProcessPages.ProcessSummaryCheckPeriods.checklistsToComplete')}</h4>
        <div className={`w-full flex flex-col gap-2`}>
            {pendingCheckPeriods && pendingCheckPeriods.map((thisCheckPeriod: any, index: number) => {
                const checklistId = thisCheckPeriod && thisCheckPeriod.ChecklistId
                const thisChecklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
                return <CollapsableCheckPeriodSummary
                    key={index}
                    checklistId={checklistId}
                    thisChecklist={thisChecklist}
                    checkPeriodId={thisCheckPeriod.Id}
                    thisCheckPeriod={thisCheckPeriod}
                    expandedByDefault={true}
                    showContinueButton={true}
                />
            })}
        </div>
        </>}
        {completedCheckPeriods && completedCheckPeriods.length > 0 && <div className={`w-full flex flex-col gap-2 items-center`}>
            <h4 className={`text-xl font-righteous text-brandblue`}>{t('ProcessPages.ProcessSummaryCheckPeriods.completedChecklists')}</h4>

        <div className={`w-full flex flex-col gap-2`}>
            {completedCheckPeriods.map((thisCheckPeriod: any, index: number) => {
                 const checklistId = thisCheckPeriod && thisCheckPeriod.ChecklistId
                const thisChecklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
                if (!thisChecklist){ 
                    console.error(`No checklist found for ID: ${checklistId} in completed check periods`)
                    return null 
                }
                return <CollapsableCheckPeriodSummary
                    key={index}
                    checklistId={checklistId}
                    thisChecklist={thisChecklist}
                    checkPeriodId={thisCheckPeriod.Id}
                    thisCheckPeriod={thisCheckPeriod}
                />
            })}   
        </div>
        </div>}
    </div>



}
export default ProcessSummaryCheckPeriods